import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { UserStatus } from "./model/constants";
import {
  CreateUserDialog,
  EditUserDialog,
  UserFilters,
  UserPermissionsModal,
  UserStatistics,
  UserTable,
} from "./ui";

import {
  useDeleteUser,
  useGetUserAggregate,
  useGetUsers,
  useSetUserBlocking,
  type UserPageItemDTO,
} from "@/common/api";
import { TablePagination } from "@/common/components";
import { useToast } from "@/common/hooks";
import { UserRoleEnum } from "@/common/model";

const LIMIT = 10;
const PAGE = 1;

export function UserManagementPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [permissionsUser, setPermissionsUser] = useState<UserPageItemDTO | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // фильтры статистики
  const [status, setStatus] = useState<UserStatus>(UserStatus.All);

  // Фильтры
  const [companyFilter, setCompanyFilter] = useState("");
  const [innFilter, setInnFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Пагинация
  const [page, setPage] = useState(PAGE);

  const roleApiValue =
    roleFilter === "admin"
      ? UserRoleEnum.Admin
      : roleFilter === "cargo_owner"
        ? UserRoleEnum.CargoOwner
        : roleFilter === "wagon_owner"
          ? UserRoleEnum.WagonOwner
          : undefined;

  // Получаем агрегированные данные пользователей
  const { data: userAggregateResponse, refetch: refetchUserAggregate } = useGetUserAggregate({
    query: {
      retry: false,
      staleTime: 0,
      refetchOnMount: true,
    },
  });

  // Получаем пагинированные данные пользователей с фильтрами
  const {
    data: usersResponse,
    isLoading,
    refetch: refetchUsers,
    queryKey: usersQueryKey,
  } = useGetUsers(
    {
      page,
      limit: LIMIT,
      role: roleApiValue,
      company: companyFilter || undefined,
      inn: innFilter || undefined,
      isActive: status === UserStatus.Blocked ? false : undefined,
    },
    {
      query: {
        retry: false,
        staleTime: 0,
        refetchOnMount: true,
      },
    },
  );

  const usersData = usersResponse?.data.payload;

  const paginatedUsers = usersData?.items || [];
  const totalItems = usersData?.totalItems || 0;
  const totalPages = usersData?.totalPage || 0;

  // Сбрасываем страницу при изменении фильтров
  useEffect(() => {
    setPage(PAGE);
  }, [companyFilter, innFilter, roleFilter, status]);

  // Используем данные из userAggregateResponse для статистики
  const aggregateData = userAggregateResponse?.data.payload;
  const totalUsers = aggregateData?.totalUsers || 0;
  const blockedUsers = aggregateData?.blockedUsers || 0;
  const adminUsers = aggregateData?.adminUsers || 0;

  // Мутация для удаления пользователя
  const { mutateAsync: deleteUserAsync } = useDeleteUser();

  // Обработчик подтверждения удаления пользователя
  const handleDeleteUser = useCallback(
    (userId: string) => {
      deleteUserAsync({
        params: { userId },
      })
        .then(() => {
          refetchUsers();
          refetchUserAggregate();
        })
        .then(() => {
          toast({
            title: "Успешно",
            description: "Пользователь удален",
          });
        })
        .catch((error: unknown) => {
          toast({
            variant: "destructive",
            title: "Ошибка удаления пользователя",
            description: error instanceof Error ? error.message : "Не удалось удалить пользователя",
          });
        });
    },
    [deleteUserAsync, refetchUsers, refetchUserAggregate, toast],
  );

  // Мутация для блокировки/разблокировки пользователя
  const { mutateAsync: setUserBlockingAsync } = useSetUserBlocking();

  // Обработчик подтверждения блокировки/разблокировки пользователя
  const handleConfirmBlocking = useCallback(
    (userId: string, isBlocking: boolean) => {
      setUserBlockingAsync({
        data: {
          userId,
          isBlocking,
        },
      })
        .then(() => {
          refetchUsers();
          refetchUserAggregate();
        })
        .then(() => {
          toast({
            title: "Успешно",
            description: isBlocking ? "Пользователь заблокирован" : "Пользователь разблокирован",
          });
        })
        .catch((error: unknown) => {
          toast({
            variant: "destructive",
            title: "Ошибка",
            description:
              error instanceof Error ? error.message : "Не удалось изменить статус пользователя",
          });
        });
    },
    [setUserBlockingAsync, refetchUsers, refetchUserAggregate, toast],
  );

  const handleEditUser = useCallback((userId: string) => {
    // Для редактирования нам достаточно идентификатора,
    // сами данные подтянем в EditUserDialog через useGetUserData.
    setEditingUser(userId);
  }, []);

  const handleResetFilters = useCallback(() => {
    setCompanyFilter("");
    setInnFilter("");
    setRoleFilter("all");
    setStatus(UserStatus.All);
    setPage(PAGE);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditingUser(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    if (usersQueryKey) {
      queryClient.invalidateQueries({ queryKey: usersQueryKey });
    }
    setEditingUser(null);
  }, [queryClient, usersQueryKey]);

  const handleStatusChange = useCallback((newStatus: UserStatus) => {
    setStatus(newStatus);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center items-start gap-5">
        <div>
          <h1 className="text-2xl font-bold text-primary">Управление пользователями</h1>
          <p className="text-muted-foreground">Управление учетными записями и правами доступа</p>
        </div>

        <CreateUserDialog isOpen={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
      </div>

      {/* Statistics */}
      <UserStatistics
        totalUsers={totalUsers}
        blockedUsers={blockedUsers}
        adminUsers={adminUsers}
        status={status}
        onStatusChange={handleStatusChange}
        onRoleFilterChange={setRoleFilter}
        onResetFilters={handleResetFilters}
      />

      {/* Filters */}
      <UserFilters
        companyFilter={companyFilter}
        innFilter={innFilter}
        roleFilter={roleFilter}
        status={status}
        onCompanyFilterChange={setCompanyFilter}
        onInnFilterChange={setInnFilter}
        onRoleFilterChange={setRoleFilter}
        onReset={handleResetFilters}
      />

      {/* Users Table */}
      <UserTable
        isLoading={isLoading}
        users={paginatedUsers}
        onEditUser={handleEditUser}
        onOpenPermissions={setPermissionsUser}
        onConfirmDelete={handleDeleteUser}
        onConfirmBlocking={handleConfirmBlocking}
      />

      {/* Pagination */}
      <div className="mt-6">
        <TablePagination
          currentPage={page}
          totalPages={totalPages ?? 0}
          totalItems={totalItems ?? 0}
          pageSize={LIMIT}
          onPageChange={(page) => {
            setPage(page);
          }}
        />
      </div>

      {/* Edit User Dialog */}
      {editingUser && (
        <EditUserDialog
          userId={editingUser}
          isOpen={!!editingUser}
          onClose={handleCloseEditDialog}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* User Permissions Modal */}
      <UserPermissionsModal
        user={permissionsUser}
        isOpen={!!permissionsUser}
        onClose={() => setPermissionsUser(null)}
      />
    </div>
  );
}
